import { useState } from 'react'
import { useParteDelDia, type GrupoReparto } from '../../hooks/usePartesReparto'
import { IconoUbicacion, IconoCorrecto, IconoFactura, IconoLogistica } from '../../componentes/iconos'

/** Color de punto por etiqueta de grupo (reutiliza los tokens de ruta del tema). */
function colorGrupo(color: string | null, indice: number): string {
  const tok = color ?? `ruta${(indice % 5) + 1}`
  return `var(--color-${tok})`
}

function horaCorta(iso: string): string {
  try {
    return new Date(iso).toLocaleTimeString('es-ES', { hour: '2-digit', minute: '2-digit' })
  } catch {
    return ''
  }
}

/** Paradas de un grupo, en orden de visita, con la nota de entrega destacada. */
function ParadasGrupo({ grupo }: { grupo: GrupoReparto }) {
  return (
    <ol className="relative ml-1 border-l border-borde pl-4">
      {grupo.paradas.map((p) => (
        <li key={`${p.orden}-${p.contactId ?? p.nombre}`} className="relative py-1.5">
          <span className="absolute -left-[21px] top-2.5 size-2.5 rounded-full bg-acento ring-2 ring-superficie" aria-hidden />
          <p className="text-sm font-medium text-tinta">
            {p.orden} · {p.nombre}
          </p>
          {p.direccion ? <p className="text-xs text-tinta-apagada">{p.direccion}</p> : null}
          {p.notaEntrega ? (
            <div className="mt-1 flex items-start gap-2 rounded-lg border border-aviso-borde bg-aviso-fondo px-2.5 py-1.5">
              <IconoFactura className="mt-0.5 size-3.5 shrink-0 text-aviso-icono" />
              <p className="text-xs font-medium text-aviso-tinta">
                <span className="font-semibold">Nota de entrega:</span> {p.notaEntrega}
              </p>
            </div>
          ) : null}
        </li>
      ))}
    </ol>
  )
}

/**
 * Ruta del día (trazabilidad): muestra, si ese día se envió la ruta al repartidor,
 * el resumen (paradas · grupos · notas), el detalle por grupo en orden de visita
 * con las notas de entrega destacadas, y el link de Google Maps por grupo.
 * Si no se envió ruta ese día, no renderiza nada (no estorba el panel).
 */
export function RutaDelDia({ iso }: { iso: string }) {
  const { data: parte, isLoading } = useParteDelDia(iso)
  const [grupoActivo, setGrupoActivo] = useState(0)

  if (isLoading || !parte || !parte.grupos?.length) return null

  const grupo = parte.grupos[grupoActivo] ?? parte.grupos[0]

  return (
    <section className="mb-4 rounded-xl border border-borde bg-lienzo/60 p-4">
      {/* Cabecera: estado enviado + hora */}
      <div className="mb-3 flex items-center justify-between gap-3">
        <h4 className="flex items-center gap-2 text-sm font-semibold text-tinta">
          <IconoLogistica className="size-4 text-acento" />
          Ruta del día
        </h4>
        <span className="inline-flex items-center gap-1.5 rounded-full border border-positivo-anillo bg-positivo-fondo px-2.5 py-1 text-xs font-semibold text-positivo-tinta">
          <IconoCorrecto className="size-3.5" />
          Enviada {horaCorta(parte.enviadoEn)}
        </span>
      </div>

      {/* Resumen numérico (estilo B) */}
      <div className="mb-3 flex gap-5 rounded-lg bg-elevado px-3 py-2.5">
        <div className="text-xs text-tinta-apagada">
          <b className="block text-lg tabular-nums text-tinta">{parte.totalParadas}</b>
          paradas
        </div>
        <div className="text-xs text-tinta-apagada">
          <b className="block text-lg tabular-nums text-tinta">{parte.totalGrupos}</b>
          grupos
        </div>
        <div className="text-xs text-tinta-apagada">
          <b className="block text-lg tabular-nums text-tinta">{parte.totalNotas}</b>
          con nota
        </div>
      </div>

      {/* Selector de grupo (si hay más de uno) */}
      {parte.grupos.length > 1 ? (
        <div className="mb-3 inline-flex overflow-hidden rounded-lg border border-borde">
          {parte.grupos.map((g, i) => (
            <button
              key={g.etiqueta}
              type="button"
              onClick={() => setGrupoActivo(i)}
              className={`flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold ${
                i === grupoActivo ? 'bg-acento text-acento-contraste' : 'text-tinta hover:bg-elevado'
              }`}
            >
              <span className="size-2 rounded-full" style={{ background: colorGrupo(g.color, i) }} aria-hidden />
              {g.etiqueta}
            </button>
          ))}
        </div>
      ) : null}

      {/* Paradas del grupo activo */}
      <ParadasGrupo grupo={grupo} />

      {/* Link de Maps del grupo activo */}
      {grupo.mapsUrl ? (
        <a
          href={grupo.mapsUrl}
          target="_blank"
          rel="noopener noreferrer"
          className="mt-3 inline-flex items-center gap-2 rounded-lg border border-acento-borde bg-acento-suave px-3 py-2 text-xs font-semibold text-acento-tinta hover:brightness-95"
        >
          <IconoUbicacion className="size-4" />
          Abrir {grupo.etiqueta} en Google Maps
        </a>
      ) : null}
    </section>
  )
}
